import { useEffect, useMemo, useRef, useState } from 'react'
import { useWallet } from '../wallet/walletContext'

function shorten(s: string, head = 6, tail = 4) {
  const v = s.trim()
  if (v.length <= head + tail + 3) return v
  return `${v.slice(0, head)}…${v.slice(-tail)}`
}

function chainLabel(chainId?: string) {
  if (!chainId) return 'EVM'
  const v = chainId.toLowerCase()
  if (v === '0x1') return 'Ethereum'
  if (v === '0x2105') return 'Base'
  if (v === '0x89') return 'Polygon'
  if (v === '0xa') return 'Optimism'
  if (v === '0x38') return 'BSC'
  return `EVM ${chainId}`
}

export default function WalletConnectButton() {
  const wallet = useWallet()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<'evm' | 'solana' | null>(null)

  const rootRef = useRef<HTMLDivElement | null>(null)

  const title = useMemo(() => {
    if (!wallet.connection) return 'Connect Wallet'
    if (wallet.connection.kind === 'evm') return `${chainLabel(wallet.connection.chainId)} · ${shorten(wallet.connection.address)}`
    return `Solana · ${shorten(wallet.connection.address)}`
  }, [wallet.connection])

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const node = rootRef.current
      if (!node) return
      if (e.target instanceof Node && node.contains(e.target)) return
      setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  async function onConnectEvm() {
    setError(null)
    setBusy('evm')
    try {
      await wallet.connectEvm()
      setOpen(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'connect_failed')
    } finally {
      setBusy(null)
    }
  }

  async function onConnectSolana() {
    setError(null)
    setBusy('solana')
    try {
      await wallet.connectSolana()
      setOpen(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'connect_failed')
    } finally {
      setBusy(null)
    }
  }

  async function onDisconnect() {
    setError(null)
    try {
      await wallet.disconnect()
    } finally {
      setOpen(false)
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80 transition hover:bg-white/10 hover:text-white"
        aria-label="Connect wallet"
      >
        {title}
      </button>

      {open ? (
        <div className="absolute right-0 mt-2 w-[320px] rounded-2xl border border-white/10 bg-bg/90 p-3 backdrop-blur">
          <div className="px-2 py-1 text-xs font-semibold text-white/70">Wallet</div>

          {wallet.connection ? (
            <div className="mt-2 rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="text-xs text-white/55">Connected</div>
              <div className="mt-1 text-sm font-semibold text-white/85">{title}</div>
              <button
                type="button"
                onClick={onDisconnect}
                className="mt-3 w-full rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-xs font-semibold text-white/80 transition hover:bg-white/10 hover:text-white"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <div className="mt-2 grid gap-2">
              <button
                type="button"
                onClick={onConnectEvm}
                disabled={!wallet.hasEvm || busy !== null}
                className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left text-sm font-semibold text-white/85 transition hover:bg-white/10 disabled:opacity-50"
              >
                <span>EVM wallet</span>
                <span className="text-xs text-white/55">{wallet.hasEvm ? 'Detected' : 'Not found'}</span>
              </button>

              <button
                type="button"
                onClick={onConnectSolana}
                disabled={!wallet.hasSolana || busy !== null}
                className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left text-sm font-semibold text-white/85 transition hover:bg-white/10 disabled:opacity-50"
              >
                <span>Solana wallet</span>
                <span className="text-xs text-white/55">{wallet.hasSolana ? 'Detected' : 'Not found'}</span>
              </button>

              <div className="rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-white/55">
                Supported (extension/injected): MetaMask, Rabby, Coinbase Wallet, Phantom, Solflare, Backpack.
              </div>
            </div>
          )}

          {error ? <div className="mt-2 px-2 text-xs text-red-300">Error: {error}</div> : null}
        </div>
      ) : null}
    </div>
  )
}
